'use client';

import { useCallback, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { toast } from 'sonner';
import { investmentsApi, type DuplicateDecisionRole } from '@/lib/api';
import { notifyError } from '@/lib/notify-error';
import { track } from '@/lib/telemetry';
import type { InvestmentItem } from '@/types';

/**
 * Merges an anchor position with the ones the user picked. The backend
 * (`POST /investments/groups`) already does the work — this expands each row to the positions
 * behind it, calls it, and reports.
 */
export function useManualGrouping(onGrouped: () => void) {
  const { getToken } = useAuth();
  const [saving, setSaving] = useState(false);

  const merge = useCallback(
    async (anchor: InvestmentItem, picked: InvestmentItem[], role: DuplicateDecisionRole) => {
      // A row may already be a group, so each one expands to every position behind it —
      // otherwise grouping a grouped row would carry only its primary and drop the rest.
      const ids = [anchor, ...picked].flatMap(
        (position) => position.sources?.map((s) => s.investmentId) ?? [position.id],
      );
      if (ids.length < 2) return false;

      setSaving(true);
      try {
        const token = await getToken();
        if (!token) throw new Error('Not authenticated');
        await investmentsApi.mergeInvestments(token, ids, role);
        track('action.investment.merge', {
          role: role === 'MIRROR' ? 'mirror' : 'additive',
          count: ids.length,
        });
        toast.success('Investimentos agrupados.');
        onGrouped();
        return true;
      } catch (err) {
        notifyError('investment_manual_merge', err, 'Não foi possível agrupar as posições.');
        return false;
      } finally {
        setSaving(false);
      }
    },
    [getToken, onGrouped],
  );

  return { saving, merge };
}

export type ManualGrouping = ReturnType<typeof useManualGrouping>;
